// ─── SEO configuration ───────────────────────────────────────────────────────
//
// Single source of truth for per-route SEO metadata. Consumed in two places:
//   1. `useSeo` (runtime) — updates <head> on client-side route changes so the
//      tab title / meta reflect the current page during SPA navigation.
//   2. `scripts/prerender.mjs` (build time) — bakes this same metadata into
//      static per-route HTML in dist/, so non-JS crawlers and social scrapers
//      (LinkedIn, WhatsApp, X, …) see real <head> tags without executing JS.
//
// Because (2) is what search/social actually rely on, the values here must be
// plain data — no runtime-only dependencies.

import { CAPABILITIES } from './site'

/** Canonical host (apex-canonical: www 301-redirects to the apex). All absolute URLs use this origin. */
export const SITE_ORIGIN = 'https://stratumlabs.tech'

/** Entity/brand name used in titles and structured data. */
export const SITE_NAME = 'Stratum Labs'

/** Default social-share image (absolute URL; falls back to the brand logo). */
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/brand/stratum-labs-logo-dark.svg`

/** Twitter card style for all pages. */
export const TWITTER_CARD = 'summary_large_image'

/** The metadata needed to render a page's <head> for SEO + social sharing. */
export interface RouteSeo {
  /** Route path, root-relative, no trailing slash except root (e.g. `/`, `/services`). */
  path: string
  /** Full <title> text (brand suffix already applied where wanted). */
  title: string
  /** Meta description (~150–160 chars ideal). */
  description: string
}

const BRAND_SUFFIX = ` | ${SITE_NAME}`

/** Build a title with the brand suffix, avoiding a doubled brand on the home page. */
export const withBrand = (title: string): string =>
  title === SITE_NAME ? title : `${title}${BRAND_SUFFIX}`

/** Static (non-parameterised) routes and their metadata. */
export const STATIC_ROUTE_SEO: RouteSeo[] = [
  {
    path: '/',
    title: SITE_NAME,
    description:
      'Stratum Labs builds and runs cloud-based websites and custom software for growing businesses, with AI woven in to cut manual work. Websites from R500/month, bespoke builds, and consulting.',
  },
  {
    path: '/services',
    title: withBrand('Services & pricing'),
    description:
      'Website-as-a-Service from R500/month, bespoke software development, and cloud & AI consulting — fully managed, well-tested, and built around your business.',
  },
  {
    path: '/products',
    title: withBrand('Talynth — POPIA compliance platform'),
    description:
      'Talynth is a self-service POPIA compliance platform for South African SMEs: a guided assessment, an instant compliance score, and auto-generated documents — in 15 minutes, not 15 weeks.',
  },
  {
    path: '/contact',
    title: withBrand('Contact us'),
    description:
      'Get in touch with Stratum Labs about websites, custom software, cloud and AI consulting, or Talynth. Tell us what you need and we will get back to you.',
  },
  {
    path: '/privacy',
    title: withBrand('Privacy policy'),
    description:
      'How Stratum Labs collects, uses, and protects personal information, in line with South Africa’s POPIA.',
  },
]

/** Per-capability detail-page metadata, derived from the capability catalogue. */
export const capabilityRouteSeo = (): RouteSeo[] =>
  CAPABILITIES.map((c) => ({
    path: `/capabilities/${c.slug}`,
    title: withBrand(c.title),
    description: c.summary,
  }))

/** Every public route's SEO metadata (static pages + capability detail pages). */
export const allRouteSeo = (): RouteSeo[] => [...STATIC_ROUTE_SEO, ...capabilityRouteSeo()]

/** Absolute canonical URL for a route path. */
export const canonicalUrl = (path: string): string =>
  path === '/' ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${path}`

/** Look up SEO metadata for a path; falls back to the home entry when unknown. */
export const seoForPath = (path: string): RouteSeo => {
  const normalised = path !== '/' && path.endsWith('/') ? path.slice(0, -1) : path
  return allRouteSeo().find((r) => r.path === normalised) ?? STATIC_ROUTE_SEO[0]
}

/** Organization JSON-LD — stable across all pages. */
export const organizationJsonLd = (): Record<string, unknown> => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  legalName: 'Stratum Labs (Pty) Ltd',
  url: `${SITE_ORIGIN}/`,
  logo: DEFAULT_OG_IMAGE,
  email: 'info@stratumlabs.tech',
  description:
    'Stratum Labs builds and runs cloud-based websites and custom software for growing businesses, with AI woven in.',
})
